import React from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { tokens } from '../../design-system/tokens';

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const pulse = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.6; }
  100% { opacity: 0.3; }
`;

const SkeletonBase = styled.div<{
  width?: string;
  height?: string;
  borderRadius?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  animationType?: 'shimmer' | 'pulse';
}>`
  display: inline-block;
  
   ${props => props.animationType === 'pulse'
    ? css`
        background: rgba(255, 255, 255, 0.2);
        animation: ${pulse} 1.5s infinite ease-in-out;
      `
    : css`
        background: linear-gradient(
          90deg,
          ${tokens.colors.basics[1200]} 25%,
          ${tokens.colors.basics[400]} 37%,
          ${tokens.colors.basics[1200]} 63%
        );
        background-size: 400px 100%;
        animation: ${shimmer} 1.4s ease-in-out infinite;
      `
  }
  
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '1em'};
  
  ${props => {
    switch (props.variant) {
      case 'circular':
        return `
          border-radius: 50%;
          width: ${props.width || props.height || '40px'};
          height: ${props.width || props.height || '40px'};
        `;
      case 'rectangular':
        return `border-radius: ${props.borderRadius || tokens.borderRadius.md};`;
      default: // text
        return `
          border-radius: ${tokens.borderRadius.sm};
          transform: scale(1, 0.6);
          transform-origin: 0 60%;
        `;
    }
  }}
`;

export interface SkeletonProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  animationType?: 'shimmer' | 'pulse';
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  borderRadius,
  variant = 'text',
  animationType = 'shimmer',
  className,
}) => {
  return (
    <SkeletonBase
      width={width}
      height={height}
      borderRadius={borderRadius}
      variant={variant}
      animationType={animationType}
      className={className}
    />
  );
};

// Componentes predefinidos para casos comunes
export const SkeletonText: React.FC<{ lines?: number; width?: string }> = ({
  lines = 1,
  width
}) => (
  <>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton
        key={i}
        variant="text"
        width={i === lines - 1 ? '60%' : width}
        height="1em"
      />
    ))}
  </>
);

export const SkeletonAvatar: React.FC<{ size?: string }> = ({ size = '40px' }) => (
  <Skeleton variant="circular" width={size} height={size} />
);

export const SkeletonButton: React.FC<{ width?: string }> = ({ width = '120px' }) => (
  <Skeleton variant="rectangular" width={width} height="40px" borderRadius={tokens.borderRadius.lg} />
);

// Skeleton para tabla
const TableSkeletonContainer = styled.div`
  width: 100%;
`;

const TableSkeletonRow = styled.div`
  display: flex;
  gap: ${tokens.spacing.md};
  padding: ${tokens.spacing.md};
  border-bottom: 1px solid ${tokens.colors.basics[1200]};
  
  &:last-child {
    border-bottom: none;
  }
`;

export const SkeletonTable: React.FC<{
  rows?: number;
  columns?: number;
  columnWidths?: string[]
}> = ({
  rows = 5,
  columns = 4,
  columnWidths = []
}) => (
    <TableSkeletonContainer>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <TableSkeletonRow key={rowIndex}>
          {Array.from({ length: columns }, (_, colIndex) => (
            <Skeleton
              key={colIndex}
              variant="text"
              width={columnWidths[colIndex] || '100px'}
              height="20px"
            />
          ))}
        </TableSkeletonRow>
      ))}
    </TableSkeletonContainer>
  );

// Skeleton para cards
const CardSkeletonContainer = styled.div`
  padding: ${tokens.spacing.lg};
  border: 1px solid ${tokens.colors.basics[1200]};
  border-radius: ${tokens.borderRadius.xl};
  background: ${tokens.colors.basics[0]};
`;

const CardSkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
  margin-bottom: ${tokens.spacing.lg};
`;

const CardSkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.sm};
`;

export const SkeletonCard: React.FC<{
  hasAvatar?: boolean;
  lines?: number
}> = ({
  hasAvatar = true,
  lines = 3
}) => (
    <CardSkeletonContainer>
      <CardSkeletonHeader>
        {hasAvatar && <SkeletonAvatar size="48px" />}
        <div style={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height="20px" />
          <Skeleton variant="text" width="40%" height="16px" />
        </div>
      </CardSkeletonHeader>
      <CardSkeletonContent>
        <SkeletonText lines={lines} />
      </CardSkeletonContent>
    </CardSkeletonContainer>
  );

export default Skeleton;
